import { useState, useEffect } from 'react';

interface AccessibilitySettings {
  fontSize: number;
  highContrast: boolean;
  grayscale: boolean;
  underlineLinks: boolean;
  reducedMotion: boolean;
  dyslexiaFont: boolean;
  cursorSize: boolean;
}

const defaultSettings: AccessibilitySettings = {
  fontSize: 100,
  highContrast: false,
  grayscale: false,
  underlineLinks: false,
  reducedMotion: false,
  dyslexiaFont: false,
  cursorSize: false,
};

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState<AccessibilitySettings>(defaultSettings);

  useEffect(() => {
    const root = document.documentElement;
    root.style.fontSize = `${settings.fontSize}%`;

    if (settings.highContrast) {
      document.body.classList.add('high-contrast');
    } else {
      document.body.classList.remove('high-contrast');
    }

    if (settings.grayscale) {
      document.body.style.filter = 'grayscale(100%)';
    } else {
      document.body.style.filter = '';
    }

    if (settings.underlineLinks) {
      document.body.classList.add('underline-links');
    } else {
      document.body.classList.remove('underline-links');
    }

    if (settings.reducedMotion) {
      document.body.classList.add('reduced-motion');
    } else {
      document.body.classList.remove('reduced-motion');
    }

    if (settings.dyslexiaFont) {
      document.body.style.fontFamily = 'Arial, sans-serif';
      document.body.style.letterSpacing = '0.05em';
      document.body.style.wordSpacing = '0.1em';
      document.body.style.lineHeight = '1.8';
    } else {
      document.body.style.fontFamily = '';
      document.body.style.letterSpacing = '';
      document.body.style.wordSpacing = '';
      document.body.style.lineHeight = '';
    }

    if (settings.cursorSize) {
      document.body.style.cursor = 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'32\' height=\'32\' viewBox=\'0 0 32 32\'%3E%3Ccircle cx=\'16\' cy=\'16\' r=\'14\' fill=\'black\' stroke=\'white\' stroke-width=\'2\'/%3E%3C/svg%3E") 16 16, auto';
    } else {
      document.body.style.cursor = '';
    }
  }, [settings]);

  const toggle = (key: keyof AccessibilitySettings) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const changeFontSize = (delta: number) => {
    setSettings(prev => ({
      ...prev,
      fontSize: Math.min(150, Math.max(80, prev.fontSize + delta)),
    }));
  };

  const resetAll = () => {
    setSettings(defaultSettings);
    document.documentElement.style.fontSize = '100%';
    document.body.style.filter = '';
    document.body.style.fontFamily = '';
    document.body.style.letterSpacing = '';
    document.body.style.wordSpacing = '';
    document.body.style.lineHeight = '';
    document.body.style.cursor = '';
    document.body.classList.remove('high-contrast', 'underline-links', 'reduced-motion');
  };

  return (
    <>
      <style>{`
        .high-contrast { filter: contrast(150%) brightness(1.1) !important; }
        .underline-links a { text-decoration: underline !important; }
        .reduced-motion *, .reduced-motion *::before, .reduced-motion *::after {
          animation-duration: 0.01ms !important;
          transition-duration: 0.01ms !important;
        }
      `}</style>

      {/* Toggle Button */}
      <button
        onClick={() => setOpen(prev => !prev)}
        aria-label="Open Accessibility Options"
        title="Accessibility Options"
        className="fixed bottom-6 left-6 z-50 w-14 h-14 rounded-full bg-teal-600 text-white shadow-xl flex items-center justify-center hover:bg-teal-700 transition-all focus:outline-none focus:ring-4 focus:ring-teal-300 cursor-pointer"
      >
        <i className="ri-body-scan-line text-2xl"></i>
      </button>

      {/* Panel */}
      {open && (
        <div
          role="dialog"
          aria-label="Accessibility Settings"
          className="fixed bottom-24 left-6 z-50 w-80 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
        >
          {/* Header */}
          <div className="bg-teal-600 px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2 text-white">
              <i className="ri-accessibility-line text-xl"></i>
              <span className="font-bold text-base">Accessibility</span>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close Accessibility Panel"
              className="text-white/80 hover:text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Font Size */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Text Size</p>
              <div className="flex items-center gap-3 bg-slate-50 rounded-xl p-3">
                <button
                  onClick={() => changeFontSize(-10)}
                  aria-label="Decrease font size"
                  className="w-9 h-9 rounded-lg bg-white border border-slate-200 text-slate-700 font-bold text-lg flex items-center justify-center hover:bg-slate-100 transition-colors cursor-pointer shadow-sm"
                >
                  A-
                </button>
                <div className="flex-1 text-center">
                  <span className="text-sm font-semibold text-slate-700">{settings.fontSize}%</span>
                </div>
                <button
                  onClick={() => changeFontSize(10)}
                  aria-label="Increase font size"
                  className="w-9 h-9 rounded-lg bg-white border border-slate-200 text-slate-700 font-bold text-lg flex items-center justify-center hover:bg-slate-100 transition-colors cursor-pointer shadow-sm"
                >
                  A+
                </button>
              </div>
            </div>

            {/* Toggle Options */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Display Options</p>
              <div className="space-y-2">
                {[
                  { key: 'highContrast' as const, icon: 'ri-contrast-2-line', label: 'High Contrast' },
                  { key: 'grayscale' as const, icon: 'ri-grayscale-line', label: 'Grayscale Mode' },
                  { key: 'underlineLinks' as const, icon: 'ri-link-m', label: 'Underline Links' },
                  { key: 'reducedMotion' as const, icon: 'ri-speed-line', label: 'Reduce Motion' },
                  { key: 'dyslexiaFont' as const, icon: 'ri-text-spacing', label: 'Dyslexia-Friendly Text' },
                  { key: 'cursorSize' as const, icon: 'ri-cursor-line', label: 'Large Cursor' },
                ].map(({ key, icon, label }) => (
                  <button
                    key={key}
                    onClick={() => toggle(key)}
                    aria-pressed={settings[key] as boolean}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-all cursor-pointer text-left ${
                      settings[key]
                        ? 'bg-teal-50 border-teal-400 text-teal-700'
                        : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    <i className={`${icon} text-lg flex-shrink-0`}></i>
                    <span className="text-sm font-medium">{label}</span>
                    {settings[key] && (
                      <i className="ri-check-line ml-auto text-teal-600"></i>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Reset */}
            <button
              onClick={resetAll}
              className="w-full py-3 rounded-xl border-2 border-slate-300 text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors cursor-pointer flex items-center justify-center gap-2"
            >
              <i className="ri-refresh-line"></i>
              Reset All Settings
            </button>

            <p className="text-xs text-slate-400 text-center leading-relaxed">
              These settings help make our website more accessible for everyone.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
